import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import './IDCaptureOverlay.css';
import { parseJwt } from '../../helpers/auth';

const defaultConfig = {
  edgeThreshold: 0.55,
  focusThreshold: 0.07,
  reflectionThreshold: 0.7,
  coverageThreshold: 0.65,
  stableRequired: 3,
  minBrightness: 130,
  maxBrightness: 220,
  captureCooldown: 3000,
  evaluationWidth: 640,
  evaluationHeight: 400,
  captureMargin: 0.1,
};

const IDCaptureOverlay = ({
  open = false,
  onClose = () => {},
  onCaptureComplete = () => {},
  config: customConfig = {},
  titleText,
  onOpenGallery,
  isUploading = false,
}) => {
  const { t, i18n } = useTranslation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const streamRef = useRef(null);
  const stableFrameCountRef = useRef(0);
  const isRtl = i18n.dir(i18n.language) === 'rtl';
  const [status, setStatus] = useState({
    state: 'waiting',
    message: t('id_capture_status_waiting'),
    hint: t('id_capture_hint_starting'),
  });
  const [scores, setScores] = useState({
    edge: 0,
    focus: 0,
    reflection: 0,
    coverage: 0,
    brightness: 0,
    stableFrames: 0,
  });
  const scoresRef = useRef({ ...scores });
  const [capture, setCapture] = useState({
    ready: false,
    inProgress: false,
    dataUrl: null,
  });

  const config = {
    ...defaultConfig,
    ...customConfig,
  };

  // debug snapshot interval id
  const debugIntervalRef = useRef(null);
  const strings = {
    title: titleText || t('id_capture_header_title'),
    closeButton: t('id_capture_button_close'),
    galleryButton: t('id_capture_button_gallery'),
    statusWaiting: t('id_capture_status_waiting'),
    statusStarting: t('id_capture_status_starting'),
    statusLooking: t('id_capture_status_looking'),
    statusReady: t('id_capture_status_ready'),
    statusCaptured: t('id_capture_status_captured'),
    hintStarting: t('id_capture_hint_starting'),
    hintPosition: t('id_capture_hint_searching'),
    hintLighting: t('id_capture_hint_lighting'),
    hintFocus: t('id_capture_hint_focus'),
    hintReflection: t('id_capture_hint_reflection'),
    hintCoverage: t('id_capture_hint_coverage'),
    hintReady: t('id_capture_hint_ready'),
    statusCameraError: t('id_capture_error_camera'),
    hintCameraError: t('id_capture_hint_camera_error'),
    retryButton: t('id_capture_button_retry', { defaultValue: 'Retry Camera' }),
    metricEdge: t('id_capture_metric_edge'),
    metricFocus: t('id_capture_metric_focus'),
    metricReflection: t('id_capture_metric_reflection'),
    metricCoverage: t('id_capture_metric_coverage'),
    metricBrightness: t('id_capture_metric_brightness'),
    metricStability: t('id_capture_metric_stability'),
    metricResolution: t('id_capture_metric_resolution', { defaultValue: 'Video size' }),
  };

  useEffect(() => {
    if (!open) {
      stopCamera();
      return undefined;
    }

    startCamera();
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = previousOverflow;
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  function updateStatus(state, message, hint) {
    setStatus({ state, message, hint });
  }

  function setThresholdState(value, threshold, isGreater = true) {
    return isGreater ? value >= threshold : value <= threshold;
  }

  const thresholdStates = {
    edge: setThresholdState(scores.edge, config.edgeThreshold),
    focus: setThresholdState(scores.focus, config.focusThreshold),
    reflection: setThresholdState(scores.reflection, config.reflectionThreshold),
    coverage: setThresholdState(scores.coverage, config.coverageThreshold),
    brightness:
      scores.brightness >= config.minBrightness && scores.brightness <= config.maxBrightness,
    stability: scores.stableFrames >= config.stableRequired,
  };

  const metricThresholds = {
    edge: `>= ${config.edgeThreshold.toFixed(2)}`,
    focus: `>= ${config.focusThreshold.toFixed(2)}`,
    reflection: `>= ${config.reflectionThreshold.toFixed(2)}`,
    coverage: `>= ${config.coverageThreshold.toFixed(2)}`,
    brightness: `${config.minBrightness}-${config.maxBrightness}`,
    stability: `>= ${config.stableRequired}`,
  };

  const videoDimensions = {
    width: videoRef.current?.videoWidth || 0,
    height: videoRef.current?.videoHeight || 0,
  };

  function isCameraApiSupported() {
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    if (!nav) {
      return false;
    }

    if (nav.mediaDevices && typeof nav.mediaDevices.getUserMedia === 'function') {
      return true;
    }

    return (
      typeof nav.getUserMedia === 'function' ||
      typeof nav.webkitGetUserMedia === 'function' ||
      typeof nav.mozGetUserMedia === 'function' ||
      typeof nav.msGetUserMedia === 'function'
    );
  }

  function getUserMedia(constraints) {
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    if (!nav) {
      return Promise.reject(new Error('Navigator is not available'));
    }

    if (nav.mediaDevices && typeof nav.mediaDevices.getUserMedia === 'function') {
      return nav.mediaDevices.getUserMedia(constraints);
    }

    const legacyGetUserMedia =
      nav.getUserMedia || nav.webkitGetUserMedia || nav.mozGetUserMedia || nav.msGetUserMedia;

    if (typeof legacyGetUserMedia === 'function') {
      return new Promise((resolve, reject) => {
        legacyGetUserMedia.call(nav, resolve, reject, constraints);
      });
    }

    return Promise.reject(new Error('getUserMedia is not supported by this browser'));
  }

  async function startCamera() {
    if (!isCameraApiSupported()) {
      const unsupportedMessage = t('id_capture_hint_camera_unsupported', {
        defaultValue: 'Camera access is not supported by this browser.',
      });
      console.error('Camera access unavailable: getUserMedia not supported');
      updateStatus('error', strings.statusCameraError, unsupportedMessage);
      return;
    }

    try {
        // the user holds the mobile in portrait during capture and we trim the sides of the video feed, so we request a higher width than height to get better quality captures
      const mediaStream = await getUserMedia({
        video: {
          facingMode: 'environment',
          // height: { ideal: 1280 },
          // width: { ideal: 960 },
          height: { ideal: 1632 },
          width: { ideal: 1224 },
          // height: { ideal: 2560 },
          // width: { ideal: 1920 },
        },
        audio: false,
      });

      streamRef.current = mediaStream;
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      stableFrameCountRef.current = 0;
      setCapture({ ready: false, inProgress: false, dataUrl: null });
      setScores({ edge: 0, focus: 0, reflection: 0, coverage: 0, brightness: 0, stableFrames: 0 });
      updateStatus('waiting', strings.statusLooking, strings.hintPosition);
      animationRef.current = requestAnimationFrame(analyzeFrame);
    } catch (error) {
      console.error('Camera access error', error);
      const hint =
        error && typeof error === 'object' && 'message' in error && error.message
          ? error.message
          : strings.hintCameraError;
      updateStatus('error', strings.statusCameraError, hint);
      stopCamera();
    }
  }

  function stopCamera() {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (error) {
          console.warn('Failed to stop track', error);
        }
      });
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }

  function getOverlayArea(width, height) {
    const overlayWidth = Math.round(width * 0.88);
    const overlayHeight = Math.round(overlayWidth / 1.6);
    const overlayX = Math.round((width - overlayWidth) / 2);
    const overlayY = Math.round((height - overlayHeight) / 2);
    const marginX = Math.round(overlayWidth * config.captureMargin);
    const marginY = Math.round(overlayHeight * config.captureMargin);
    const cropX = Math.max(0, overlayX - marginX);
    const cropY = Math.max(0, overlayY - marginY);
    const cropWidth = Math.min(width - cropX, overlayWidth + marginX * 2);
    const cropHeight = Math.min(height - cropY, overlayHeight + marginY * 2);
    return { overlayX, overlayY, overlayWidth, overlayHeight, cropX, cropY, cropWidth, cropHeight };
  }

  function getVisibleVideoCrop(video) {
    if (!video || !video.videoWidth || !video.videoHeight) {
      return null;
    }

    const displayWidth = video.clientWidth;
    const displayHeight = video.clientHeight;
    if (!displayWidth || !displayHeight) {
      return null;
    }

    const scaleX = displayWidth / video.videoWidth;
    const scaleY = displayHeight / video.videoHeight;
    const scale = Math.max(scaleX, scaleY);

    let sourceWidth = video.videoWidth;
    let sourceHeight = video.videoHeight;
    let sourceX = 0;
    let sourceY = 0;

    if (scaleX > scaleY) {
      sourceHeight = displayHeight / scale;
      sourceY = (video.videoHeight - sourceHeight) / 2;
    } else {
      sourceWidth = displayWidth / scale;
      sourceX = (video.videoWidth - sourceWidth) / 2;
    }

    return {
      sourceX: Math.max(0, Math.round(sourceX)),
      sourceY: Math.max(0, Math.round(sourceY)),
      sourceWidth: Math.max(1, Math.round(sourceWidth)),
      sourceHeight: Math.max(1, Math.round(sourceHeight)),
    };
  }

  function evaluateFrame(imageData) {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;
    let brightnessSum = 0;
    let insideEdge = 0;
    let insideEdgeSq = 0;
    let totalEdge = 0;
    let overlayBright = 0;
    let overlayPixelCount = 0;

    const { overlayX, overlayY, overlayWidth, overlayHeight } = getOverlayArea(width, height);
    const overlayX2 = overlayX + overlayWidth;
    const overlayY2 = overlayY + overlayHeight;

    for (let y = 0; y < height - 1; y++) {
      const rowOffset = y * width * 4;
      const nextRowOffset = (y + 1) * width * 4;
      const insideRow = y >= overlayY && y < overlayY2;

      for (let x = 0; x < width - 1; x++) {
        const idx = rowOffset + x * 4;
        const r = data[idx];
        const g = data[idx + 1];
        const b = data[idx + 2];
        const gray = r * 0.299 + g * 0.587 + b * 0.114;
        brightnessSum += gray;

        const rightIdx = idx + 4;
        const bottomIdx = nextRowOffset + x * 4;
        const r2 = data[rightIdx];
        const g2 = data[rightIdx + 1];
        const b2 = data[rightIdx + 2];
        const grayRight = r2 * 0.299 + g2 * 0.587 + b2 * 0.114;
        const r3 = data[bottomIdx];
        const g3 = data[bottomIdx + 1];
        const b3 = data[bottomIdx + 2];
        const grayBottom = r3 * 0.299 + g3 * 0.587 + b3 * 0.114;

        const grad = Math.abs(gray - grayRight) + Math.abs(gray - grayBottom);
        totalEdge += grad;

        if (insideRow && x >= overlayX && x < overlayX2) {
          insideEdge += grad;
          insideEdgeSq += grad * grad;
          overlayPixelCount += 1;
          if (gray > 235) {
            overlayBright += 1;
          }
        }
      }
    }

    const pixelCount = width * height;
    const meanEdgeOverlay = overlayPixelCount === 0 ? 0 : insideEdge / (overlayPixelCount * 2);
    const variance = overlayPixelCount === 0 ? 0 : insideEdgeSq / (overlayPixelCount * 2) - meanEdgeOverlay * meanEdgeOverlay;
    const score = meanEdgeOverlay / 10;
    const focus = Math.max(0, variance / 3000);
    const brightness = brightnessSum / pixelCount;
    const reflection = overlayPixelCount === 0 ? 0 : Math.max(0, 1 - overlayBright / overlayPixelCount);
    const coverage = totalEdge === 0 ? 0 : insideEdge / totalEdge;

    return { score, brightness, focus, coverage, reflection };
  }

  function captureFrame() {
    if (!videoRef.current || !videoRef.current.videoWidth || !videoRef.current.videoHeight) {
      return null;
    }

    const { cropX, cropY, cropWidth, cropHeight } = getOverlayArea(videoRef.current.videoWidth, videoRef.current.videoHeight);
    const captureCanvas = document.createElement('canvas');
    captureCanvas.width = cropWidth;
    captureCanvas.height = cropHeight;
    const ctx = captureCanvas.getContext('2d');
    ctx.drawImage(videoRef.current, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
    return captureCanvas.toDataURL('image/jpeg', 0.95);
  }

  function dataUrlToBlob(dataUrl) {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  function normalizeMetrics(metrics) {
    return {
      edge: metrics.edge.toFixed(2),
      focus: metrics.focus.toFixed(2),
      reflection: metrics.reflection.toFixed(2),
      coverage: Math.round(metrics.coverage * 100),
      brightness: Math.round(metrics.brightness),
    };
  }

  async function uploadDebugSnapshot(metrics) {
    try {
      console.debug('Debug snapshot: starting upload attempt', metrics);
      const dataUrl = captureFrame();
      if (!dataUrl) {
        console.debug('Debug snapshot: no dataUrl available');
        return;
      }

      const blob = dataUrlToBlob(dataUrl);
      const formData = new FormData();
      formData.append('file', blob, 'debug.jpg');
      formData.append('upload_preset', 'fz7jch0p');

      // determine folder from token in localStorage (matching other hooks)
      let folder = 'admin_uploads';
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const parsed = parseJwt(token);
          if (parsed && parsed.userId) folder = parsed.userId;
        }
      } catch (e) {
        // fallback to admin_uploads
      }

      const { edge, focus, reflection, coverage, brightness } = normalizeMetrics(metrics);

      // Cloudinary public_id must not include extension here
      formData.append('folder', folder);
      formData.append('public_id', `debug-${edge}-${focus}-${reflection}-${coverage}-${brightness}`);

      // Store debug values as asset-level metadata-like fields for easier search/filtering.
      formData.append('context', `debug_edge=${edge}|debug_focus=${focus}|debug_reflection=${reflection}|debug_coverage=${coverage}|debug_brightness=${brightness}`);
      formData.append('tags', `debug,debug_edge_${edge},debug_focus_${focus},debug_reflection_${reflection},debug_coverage_${coverage},debug_brightness_${brightness}`);

      const resp = await fetch('https://api.cloudinary.com/v1_1/tidp/image/upload', {
        method: 'POST',
        body: formData,
      });
      console.debug('Debug snapshot: upload response', resp && resp.status);
    } catch (err) {
      console.warn('Debug snapshot upload failed', err);
    }
  }

  async function performCapture() {
    setCapture((prev) => ({ ...prev, inProgress: true }));
    const dataUrl = captureFrame();
    if (dataUrl) {
      stopCamera();
      try {
        const metrics = normalizeMetrics(scoresRef.current);
        await onCaptureComplete(dataUrl, metrics);
      } finally {
        onClose();
      }
    }
  }

  function snapshotReady() {
    if (capture.inProgress || !videoRef.current || !videoRef.current.videoWidth || !videoRef.current.videoHeight) {
      return;
    }

    const captureWidth = config.evaluationWidth;
    const captureHeight = config.evaluationHeight;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const visibleCrop = getVisibleVideoCrop(videoRef.current);
    if (!visibleCrop) return;

    canvas.width = captureWidth;
    canvas.height = captureHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    ctx.drawImage(
      videoRef.current,
      visibleCrop.sourceX,
      visibleCrop.sourceY,
      visibleCrop.sourceWidth,
      visibleCrop.sourceHeight,
      0,
      0,
      captureWidth,
      captureHeight,
    );
    const imageData = ctx.getImageData(0, 0, captureWidth, captureHeight);
    const { score, brightness, focus, coverage, reflection } = evaluateFrame(imageData);

    const isBrightnessOk = brightness >= config.minBrightness && brightness <= config.maxBrightness;
    const isEdgeStrong = score >= config.edgeThreshold;
    const isFocusOk = focus >= config.focusThreshold;
    const isCoverageOk = coverage >= config.coverageThreshold;
    const isReflectionOk = reflection >= config.reflectionThreshold;
    const allReady = isBrightnessOk && isEdgeStrong && isFocusOk && isCoverageOk && isReflectionOk;

    if (allReady) {
      stableFrameCountRef.current += 1;
    } else {
      stableFrameCountRef.current = 0;
    }

    const stableFrames = stableFrameCountRef.current;
    const newScores = { edge: score, focus, reflection, coverage, brightness, stableFrames };
    scoresRef.current = newScores;
    setScores(newScores);

    if (stableFrames >= config.stableRequired) {
      performCapture();
      return;
    }

    if (!isBrightnessOk) {
      updateStatus('waiting', strings.statusWaiting, strings.hintLighting);
    } else if (!isFocusOk) {
      updateStatus('waiting', strings.statusWaiting, strings.hintFocus);
    } else if (!isReflectionOk) {
      updateStatus('waiting', strings.statusWaiting, strings.hintReflection);
    } else if (!isCoverageOk) {
      updateStatus('waiting', strings.statusWaiting, strings.hintCoverage);
    } else if (allReady) {
      updateStatus('ready', strings.statusReady, strings.hintReady);
    } else {
      updateStatus('waiting', strings.statusLooking, strings.hintPosition);
    }
  }

  function analyzeFrame() {
    if (!open) return;
    snapshotReady();
    animationRef.current = requestAnimationFrame(analyzeFrame);
  }

  // start/stop periodic debug snapshots when overlay opens/closes
  useEffect(() => {
    if (!open) {
      if (debugIntervalRef.current) {
        console.debug('Debug snapshot: clearing interval (overlay closed)');
        clearInterval(debugIntervalRef.current);
        debugIntervalRef.current = null;
      }
      return undefined;
    }

    if (debugIntervalRef.current) {
      console.debug('Debug snapshot: interval already running; skipping creation');
      return undefined;
    }

    // every 10s take a debug snapshot if thresholds not met
    console.debug('Debug snapshot: starting interval');
    debugIntervalRef.current = setInterval(() => {
      const s = scoresRef.current;
      const isBrightnessOk = s.brightness >= config.minBrightness && s.brightness <= config.maxBrightness;
      const isEdgeStrong = s.edge >= config.edgeThreshold;
      const isFocusOk = s.focus >= config.focusThreshold;
      const isCoverageOk = s.coverage >= config.coverageThreshold;
      const isReflectionOk = s.reflection >= config.reflectionThreshold;
      const allReady = isBrightnessOk && isEdgeStrong && isFocusOk && isCoverageOk && isReflectionOk;

      if (!allReady) {
        uploadDebugSnapshot({
          edge: s.edge,
          focus: s.focus,
          reflection: s.reflection,
          coverage: s.coverage,
          brightness: s.brightness,
        });
      } else {
        console.debug('Debug snapshot: thresholds met; skipping upload');
      }
    }, 10000);

    return () => {
      if (debugIntervalRef.current) {
        console.debug('Debug snapshot: clearing interval');
        clearInterval(debugIntervalRef.current);
        debugIntervalRef.current = null;
      }
    };
  }, [open, config.edgeThreshold, config.focusThreshold, config.coverageThreshold, config.reflectionThreshold, config.minBrightness, config.maxBrightness]);

  function handleRetryCamera() {
    stopCamera();
    updateStatus('waiting', strings.statusStarting, strings.hintStarting);
    startCamera();
  }

  if (!open) {
    return null;
  }

  return (
    <div className={`id-capture-overlay ${isRtl ? 'rtl' : ''}`} role="dialog" aria-modal="true" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="id-capture-panel">
        <div className="id-capture-header">
          <div>
            <h2>{strings.title}</h2>
          </div>
        </div>

        <div className="id-capture-camera-stage">
          <video ref={videoRef} autoPlay muted playsInline className="id-capture-video" />
          <div className="id-capture-overlay-frame" aria-hidden="true" />

          {status.state === 'error' && (
            <div className="id-capture-error-panel">
              <strong>{status.message}</strong>
              <div className="id-capture-error-hint">{status.hint}</div>
              <button className="btn btn-secondary" type="button" onClick={handleRetryCamera}>
                {strings.retryButton}
              </button>
            </div>
          )}

          {isUploading && (
            <div className="id-capture-uploading-panel" role="status" aria-live="polite" aria-busy="true">
              <Spinner animation="border" variant="light" />
              <div className="id-capture-uploading-text">
                {t('id_capture_uploading', { defaultValue: 'Uploading image...' })}
              </div>
            </div>
          )}

        </div>

        <div className="id-capture-status-pill">
          <span className={`indicator ${status.state === 'captured' ? 'indicator-success' : status.state === 'ready' ? 'indicator-ready' : 'indicator-waiting'}`} />
          <div>
            <strong>{status.message}</strong>
            <div className="id-capture-hint">{status.hint}</div>
          </div>
        </div>

        <div className="id-capture-metrics">
          <div className="metric-row">
            <span>{strings.metricEdge}</span>
            <span className="metric-value">
              {scores.edge.toFixed(2)}
              <span className="metric-threshold">{metricThresholds.edge}</span>
            </span>
            <span className={`metric-status ${thresholdStates.edge ? 'good' : 'bad'}`}>{thresholdStates.edge ? '✓' : '✕'}</span>
          </div>
          <div className="metric-row">
            <span>{strings.metricFocus}</span>
            <span className="metric-value">
              {scores.focus.toFixed(2)}
              <span className="metric-threshold">{metricThresholds.focus}</span>
            </span>
            <span className={`metric-status ${thresholdStates.focus ? 'good' : 'bad'}`}>{thresholdStates.focus ? '✓' : '✕'}</span>
          </div>
          <div className="metric-row">
            <span>{strings.metricReflection}</span>
            <span className="metric-value">
              {scores.reflection.toFixed(2)}
              <span className="metric-threshold">{metricThresholds.reflection}</span>
            </span>
            <span className={`metric-status ${thresholdStates.reflection ? 'good' : 'bad'}`}>{thresholdStates.reflection ? '✓' : '✕'}</span>
          </div>
          <div className="metric-row">
            <span>{strings.metricCoverage}</span>
            <span className="metric-value">
              {Math.round(scores.coverage * 100)}%
              <span className="metric-threshold">{metricThresholds.coverage}</span>
            </span>
            <span className={`metric-status ${thresholdStates.coverage ? 'good' : 'bad'}`}>{thresholdStates.coverage ? '✓' : '✕'}</span>
          </div>
          <div className="metric-row">
            <span>{strings.metricResolution}</span>
            <span className="metric-value">
              {videoDimensions.width}×{videoDimensions.height}
            </span>
            <span className="metric-status" />
          </div>
          <div className="metric-row">
            <span>{strings.metricBrightness}</span>
            <span className="metric-value">
              {scores.brightness.toFixed(0)}
              <span className="metric-threshold">{metricThresholds.brightness}</span>
            </span>
            <span className={`metric-status ${thresholdStates.brightness ? 'good' : 'bad'}`}>{thresholdStates.brightness ? '✓' : '✕'}</span>
          </div>
          <div className="metric-row">
            <span>{strings.metricStability}</span>
            <span className="metric-value">
              {scores.stableFrames}
              <span className="metric-threshold">{metricThresholds.stability}</span>
            </span>
            <span className={`metric-status ${thresholdStates.stability ? 'good' : 'bad'}`}>{thresholdStates.stability ? '✓' : '✕'}</span>
          </div>
        </div>

        <div className="id-capture-actions">
          {onOpenGallery && (
            <button className="btn btn-secondary" type="button" onClick={() => {
              onClose();
              setTimeout(() => {
                onOpenGallery();
              }, 0);
            }}>
              {strings.galleryButton}
            </button>
          )}
          <button className="btn btn-secondary" type="button" onClick={onClose}>{strings.closeButton}</button>
        </div>
      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default IDCaptureOverlay;
